import { createContext, useContext, useCallback, useMemo } from 'react';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { tourRegistry } from '../config';
import {
  TourContextValue,
  TourProgress,
  TourState,
  DEFAULT_TOUR_PROGRESS,
} from '../types';

const TourContext = createContext<TourContextValue | null>(null);

export function TourProvider({ children }: { children: React.ReactNode }) {
  const [progress, setProgress] = useLocalStorage<TourProgress>(
    'sv-tour-progress',
    DEFAULT_TOUR_PROGRESS
  );
  const [activeTourId, setActiveTourId] = useLocalStorage<string | null>(
    'sv-tour-active',
    null
  );
  const [currentStepIndex, setCurrentStepIndex] = useLocalStorage<number>(
    'sv-tour-step',
    0
  );

  const state: TourState = useMemo(
    () => ({ activeTourId, currentStepIndex, progress }),
    [activeTourId, currentStepIndex, progress]
  );

  const activeTour = useMemo(
    () => (activeTourId ? tourRegistry[activeTourId] ?? null : null),
    [activeTourId]
  );
  const activeStep = useMemo(
    () => activeTour?.steps[currentStepIndex] ?? null,
    [activeTour, currentStepIndex]
  );
  const isActive = activeTour !== null && activeStep !== null;

  const startTour = useCallback(
    (tourId: string) => {
      const tour = tourRegistry[tourId];
      if (!tour) return;
      // Don't restart completed tours unless explicitly reset
      if (progress.completedTours.includes(tourId)) return;
      if (progress.skippedTours.includes(tourId)) return;
      setActiveTourId(tourId);
      setCurrentStepIndex(0);
    },
    [progress, setActiveTourId, setCurrentStepIndex]
  );

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    if (currentStepIndex < activeTour.steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      // Last step — complete the tour
      setProgress((prev) => ({
        ...prev,
        completedTours: [...new Set([...prev.completedTours, activeTour.id])],
      }));
      setActiveTourId(null);
      setCurrentStepIndex(0);
    }
  }, [activeTour, currentStepIndex, setActiveTourId, setCurrentStepIndex, setProgress]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex, setCurrentStepIndex]);

  const skipTour = useCallback(() => {
    if (!activeTour) return;
    setProgress((prev) => ({
      ...prev,
      skippedTours: [...new Set([...prev.skippedTours, activeTour.id])],
    }));
    setActiveTourId(null);
    setCurrentStepIndex(0);
  }, [activeTour, setActiveTourId, setCurrentStepIndex, setProgress]);

  const completeTour = useCallback(() => {
    if (!activeTour) return;
    setProgress((prev) => ({
      ...prev,
      completedTours: [...new Set([...prev.completedTours, activeTour.id])],
    }));
    setActiveTourId(null);
    setCurrentStepIndex(0);
  }, [activeTour, setActiveTourId, setCurrentStepIndex, setProgress]);

  const resetTour = useCallback(
    (tourId: string) => {
      setProgress((prev) => ({
        completedTours: prev.completedTours.filter((id) => id !== tourId),
        skippedTours: prev.skippedTours.filter((id) => id !== tourId),
      }));
      // If this was the active tour, clear it
      if (activeTourId === tourId) {
        setActiveTourId(null);
        setCurrentStepIndex(0);
      }
    },
    [activeTourId, setActiveTourId, setCurrentStepIndex, setProgress]
  );

  const resetAllTours = useCallback(() => {
    setProgress(DEFAULT_TOUR_PROGRESS);
    setActiveTourId(null);
    setCurrentStepIndex(0);
  }, [setProgress, setActiveTourId, setCurrentStepIndex]);

  const isTourCompleted = useCallback(
    (tourId: string) =>
      progress.completedTours.includes(tourId) ||
      progress.skippedTours.includes(tourId),
    [progress]
  );

  const value: TourContextValue = useMemo(
    () => ({
      state,
      activeTour,
      activeStep,
      isActive,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      resetTour,
      resetAllTours,
      isTourCompleted,
    }),
    [
      state,
      activeTour,
      activeStep,
      isActive,
      startTour,
      nextStep,
      prevStep,
      skipTour,
      completeTour,
      resetTour,
      resetAllTours,
      isTourCompleted,
    ]
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}

export function useTourContext(): TourContextValue {
  const ctx = useContext(TourContext);
  if (!ctx) {
    throw new Error('useTourContext must be used within a TourProvider');
  }
  return ctx;
}
